/**
 * Reading parties — customers and suppliers — out of the database.
 *
 * WHY A SEPARATE FILE, mirroring `documentReader.ts`. That file is the WRITE
 * path's read half and is the only place a transaction may be opened for a
 * document. A read opens none: there is no atomicity to protect and no number to
 * allocate. Mixing them would drag a transaction scope into every list screen.
 *
 * THE COMPANY BOUNDARY IS THE WHOLE POINT
 * ----------------------------------------
 * `party.id` is a global primary key, exactly like `document.id`, and exactly
 * like it, `db.parties.get(id)` will happily return another company's customer.
 * With two real companies that is a customer list belonging to the wrong GSTIN,
 * one click away from being attached to that company's invoice.
 *
 * So every function here takes the business id explicitly and filters on it in
 * the query itself — not in JavaScript afterwards, where a forgotten `.filter`
 * is a silent, total leak. The `[businessId+name]` index already declared on the
 * table means this is an index seek rather than a scan.
 *
 * A single party is REFUSED across the company boundary, matching
 * `loadDocument`'s `WRONG_BUSINESS`. Hiding a row from a list is not the same as
 * making it unreachable: an old bookmark still resolves, and it must not resolve
 * to another company's customer.
 */
import type { Table } from 'dexie';
import type { Party, PartyKind } from '../db/types';

/** Why a party could not be read. Each one is a state the user must be told about. */
export class PartyReadError extends Error {
  constructor(
    readonly code: 'NOT_FOUND' | 'WRONG_BUSINESS',
    message: string,
  ) {
    super(message);
    this.name = 'PartyReadError';
  }
}

/** The one table a party read needs. */
export interface PartyReadTables {
  parties: Table<Party, string>;
}

/** What a list screen can ask for. */
export interface ListPartiesOptions {
  /**
   * `undefined` means BOTH kinds. A customer list that silently included
   * suppliers would double-count a name that is legitimately both, and the
   * user has no way to tell which is which on screen.
   */
  readonly kind?: PartyKind;
  /**
   * Hide inactive records. Default FALSE — a deactivated customer still has
   * invoices against them, and a ledger that hides them is a ledger that
   * cannot be reconciled.
   */
  readonly activeOnly?: boolean;
  /** Case-insensitive substring over name, code and GSTIN. */
  readonly search?: string;
  /** Default 500: past this a list screen is a report, not a list. */
  readonly limit?: number;
}

const DEFAULT_LIMIT = 500;

/**
 * The parties belonging to one company.
 *
 * Sorted by name, and then by code, so the order is TOTAL. Two customers with
 * the same name — which happens in real ledgers, "SYNTHETIC TRADING CO" appears
 * many times in the imported backup — must have a stable order or the list
 * reshuffles between renders and a click lands on the wrong row.
 */
export async function listParties(
  db: PartyReadTables,
  businessId: string,
  options: ListPartiesOptions = {},
): Promise<Party[]> {
  const { kind, activeOnly = false, search } = options;
  const limit = Math.max(0, options.limit ?? DEFAULT_LIMIT);
  if (limit === 0) return [];

  // The company filter lives in the QUERY, not in a JavaScript pass afterwards.
  // Every constraint the caller cannot forget must be part of the index seek, so
  // a screen that forgets to filter is a type error rather than a total leak.
  const query = db.parties.where('businessId').equals(businessId);

  const rows = await query.toArray();

  // `kind` and `activeOnly` are post-filters, and deliberately so: the compound
  // indexes are [businessId+kind] and [businessId+isActive], but combining both
  // with a name search would need a third index for a screen that lists a few
  // hundred rows. The company filter — the one that must never be missed — is
  // the one that is done in the query.
  let filtered = rows;
  if (kind !== undefined) filtered = filtered.filter((p) => p.kind === kind);
  if (activeOnly) filtered = filtered.filter((p) => p.isActive);

  const q = search?.trim().toLowerCase();
  if (q) {
    filtered = filtered.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        (p.gstin?.toLowerCase().includes(q) ?? false),
    );
  }

  return filtered
    .sort((a, b) => a.name.localeCompare(b.name) || a.code.localeCompare(b.code))
    .slice(0, limit);
}

/**
 * One party, for a screen that needs more than the list row carries.
 *
 * @throws PartyReadError NOT_FOUND when no such row exists, WRONG_BUSINESS when
 *         it exists but belongs to a different company.
 */
export async function loadParty(
  db: PartyReadTables,
  businessId: string,
  partyId: string,
): Promise<Party> {
  const party = await db.parties.get(partyId);
  if (!party) {
    throw new PartyReadError(
      'NOT_FOUND',
      `No ${partyId === '' ? 'party' : `party "${partyId}"`} exists. Refusing to show a ` +
        'blank panel that looks like a real customer.',
    );
  }
  if (party.businessId !== businessId) {
    throw new PartyReadError(
      'WRONG_BUSINESS',
      `That contact belongs to another company, not the one currently open. Refusing, ` +
        'because attaching another company\'s customer to an invoice puts the wrong GSTIN ' +
        'on a real tax document.',
    );
  }
  return party;
}
